import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";

const EMPTY_SUMMARY = Object.freeze({
  reactions: Object.freeze({
    love: 0,
    jealous: 0,
    fire: 0,
    total: 0,
  }),
  comments: Object.freeze({
    count: 0,
    latest: null,
  }),
});

function normalizeIds(itemIds) {
  return [...new Set(
    (itemIds || [])
      .filter((id) => id !== null && id !== undefined && id !== "")
      .map((id) => String(id))
  )].sort();
}

function buildSummary(row) {
  const love = Number(row?.love_count || 0);
  const jealous = Number(row?.jealous_count || 0);
  const fire = Number(row?.fire_count || 0);

  const latest =
    row?.latest_comment_id
      ? {
          id: row.latest_comment_id,
          text: row.latest_comment_text || "",
          createdAt: row.latest_comment_created_at || null,
          userId: row.latest_comment_user_id || null,
          displayName:
            row.latest_comment_display_name ||
            "collector",
        }
      : null;

  return {
    reactions: {
      love,
      jealous,
      fire,
      total: love + jealous + fire,
    },
    comments: {
      count: Number(row?.comment_count || 0),
      latest,
    },
  };
}

/**
 * One batched social-summary query for the currently rendered public items.
 *
 * Why this exists:
 * - Avoids one reaction query per card.
 * - Avoids one comment-count query per card.
 * - Gives every public card the same social model.
 * - Uses one realtime subscription per table, not one per card.
 */
export default function usePublicItemSocialSummaries(itemIds) {
  const normalizedIds = useMemo(
    () => normalizeIds(itemIds),
    [JSON.stringify(normalizeIds(itemIds))]
  );

  const idsKey = normalizedIds.join("|");
  const idsSetRef = useRef(new Set(normalizedIds));
  const refreshTimerRef = useRef(null);

  const [summaryById, setSummaryById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    idsSetRef.current = new Set(normalizedIds);
  }, [idsKey]);

  const refresh = useCallback(async () => {
    if (!normalizedIds.length) {
      setSummaryById({});
      setError(null);
      setLoading(false);
      return {};
    }

    setLoading(true);

    const { data, error: rpcError } = await supabase.rpc(
      "get_public_item_social_summaries",
      {
        p_target_ids: normalizedIds,
      }
    );

    if (rpcError) {
      console.error(
        "Failed to load public item social summaries:",
        rpcError
      );
      setError(rpcError);
      setLoading(false);
      return {};
    }

    const next = Object.fromEntries(
      normalizedIds.map((id) => [
        id,
        {
          reactions: { ...EMPTY_SUMMARY.reactions },
          comments: { ...EMPTY_SUMMARY.comments },
        },
      ])
    );

    (data || []).forEach((row) => {
      next[String(row.target_id)] = buildSummary(row);
    });

    setSummaryById(next);
    setError(null);
    setLoading(false);

    return next;
  }, [idsKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Coalesce bursts of realtime events into one RPC refresh.
   * A new comment can produce more than one DB event in quick succession,
   * and reaction changes can be UPDATEs rather than INSERTs.
   */
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      refresh();
    }, 120);
  }, [refresh]);

  useEffect(() => {
    if (!normalizedIds.length) return undefined;

    const affectsVisibleItem = (payload) => {
      const row =
        payload?.new && Object.keys(payload.new).length
          ? payload.new
          : payload?.old || {};

      // DELETE payloads can be sparse unless replica identity is FULL.
      // Refresh conservatively if we cannot inspect the deleted row.
      if (!row || !Object.keys(row).length) return true;

      if (
        row.target_type &&
        row.target_type !== "user_item"
      ) {
        return false;
      }

      if (
        row.target_id === null ||
        row.target_id === undefined
      ) {
        return true;
      }

      return idsSetRef.current.has(String(row.target_id));
    };

    const commentsChannel = supabase
      .channel(`hub-social-comments-${idsKey}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "social_comments",
        },
        (payload) => {
          if (affectsVisibleItem(payload)) {
            scheduleRefresh();
          }
        }
      )
      .subscribe();

    const reactionsChannel = supabase
      .channel(`hub-social-reactions-${idsKey}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "social_interactions",
        },
        (payload) => {
          if (affectsVisibleItem(payload)) {
            scheduleRefresh();
          }
        }
      )
      .subscribe();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      supabase.removeChannel(commentsChannel);
      supabase.removeChannel(reactionsChannel);
    };
  }, [idsKey, scheduleRefresh]);

  const getSummary = useCallback(
    (itemId) =>
      summaryById[String(itemId)] || {
        reactions: { ...EMPTY_SUMMARY.reactions },
        comments: { ...EMPTY_SUMMARY.comments },
      },
    [summaryById]
  );

  return {
    summaryById,
    getSummary,
    loading,
    error,
    refresh,
  };
}
